import {
  useEffect,
  useMemo,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ViewInArRoundedIcon from '@mui/icons-material/ViewInArRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import type {
  UpdateWarehouseMapPayload,
  UpsertWarehouseMapCellPayload,
  Warehouse,
  WarehouseLocation,
  WarehouseMap,
  WarehouseMapCell,
  WarehouseMapCellType,
  WarehouseMapStatus,
  WarehouseLocationMaterialAssignment,
} from '../types/industrialCost';
import {
  activateIndustrialCostWarehouseMap,
  createIndustrialCostWarehouse,
  createIndustrialCostWarehouseMap,
  getIndustrialCostWarehouseMapDetail,
  getIndustrialCostWarehouseMaps,
  getIndustrialCostWarehouses,
  replaceIndustrialCostWarehouseMapCells,
  updateIndustrialCostWarehouse,
  updateIndustrialCostWarehouseMap,
  deleteIndustrialCostWarehouseLocationMaterialAssignment,
  getIndustrialCostWarehouseLocationMaterialAssignments,
  upsertIndustrialCostWarehouseLocationMaterialAssignment,
} from '../services/industrialCostService';

type WarehouseBrush = WarehouseMapCellType | 'erase';

type LocalWarehouseCell = {
  grid_x: number;
  grid_y: number;

  cell_type: WarehouseMapCellType;
  levels_count: number;

  label: string | null;
  notes: string | null;
};

type WarehouseMapDraft = {
  code: string;
  name: string;
  description: string;

  grid_width: number;
  grid_height: number;

  status: WarehouseMapStatus;
  is_active: boolean;
};

const MIN_GRID_SIZE = 4;
const MAX_GRID_SIZE = 60;

const CELL_SIZE = 34;
const CELL_GAP = 3;

const BRUSH_OPTIONS: Array<{
  value: WarehouseBrush;
  label: string;
  helper: string;
  icon: ReactNode;
}> = [
  {
    value: 'aisle',
    label: 'Pasillo',
    helper: 'Zona transitable',
    icon: <GridViewRoundedIcon fontSize="small" />,
  },
  {
    value: 'rack',
    label: 'Estantería',
    helper: 'Huecos para palés',
    icon: <ViewInArRoundedIcon fontSize="small" />,
  },
  {
    value: 'floor_storage',
    label: 'Suelo',
    helper: 'Almacenamiento en superficie',
    icon: <LocalShippingRoundedIcon fontSize="small" />,
  },
  {
    value: 'blocked',
    label: 'Bloqueado',
    helper: 'Espacio no utilizable',
    icon: <BlockRoundedIcon fontSize="small" />,
  },
  {
    value: 'erase',
    label: 'Borrar',
    helper: 'Dejar celda vacía',
    icon: <ClearRoundedIcon fontSize="small" />,
  },
];

function clampGridSize(value: number) {
  if (!Number.isFinite(value)) return MIN_GRID_SIZE;

  return Math.min(
    Math.max(Math.round(value), MIN_GRID_SIZE),
    MAX_GRID_SIZE,
  );
}

function buildCellKey(gridX: number, gridY: number) {
  return `${gridX}:${gridY}`;
}

function buildCellLabel(gridX: number, gridY: number) {
  return `X${String(gridX + 1).padStart(2, '0')}-Y${String(gridY + 1).padStart(2, '0')}`;
}

function getCellTypeLabel(cellType: WarehouseMapCellType) {
  switch (cellType) {
    case 'aisle':
      return 'Pasillo';
    case 'rack':
      return 'Estantería';
    case 'floor_storage':
      return 'Almacenamiento en suelo';
    case 'blocked':
      return 'Bloqueado';
    default:
      return cellType;
  }
}

function getCellVisualSx(cell: LocalWarehouseCell | null) {
  if (!cell) {
    return {
      bgcolor: 'rgba(255,255,255,0.025)',
      borderColor: 'rgba(148,163,184,0.12)',
      color: '#64748b',
    };
  }

  switch (cell.cell_type) {
    case 'aisle':
      return {
        bgcolor: 'rgba(34,197,94,0.62)',
        borderColor: 'rgba(134,239,172,0.84)',
        color: '#052e16',
      };
    case 'rack':
      return {
        bgcolor: 'rgba(239,68,68,0.74)',
        borderColor: 'rgba(254,202,202,0.88)',
        color: '#450a0a',
      };
    case 'floor_storage':
      return {
        bgcolor: 'rgba(245,158,11,0.72)',
        borderColor: 'rgba(253,230,138,0.88)',
        color: '#451a03',
      };
    case 'blocked':
      return {
        bgcolor: 'rgba(71,85,105,0.88)',
        borderColor: 'rgba(148,163,184,0.72)',
        color: '#e2e8f0',
      };
    default:
      return {
        bgcolor: 'rgba(255,255,255,0.025)',
        borderColor: 'rgba(148,163,184,0.12)',
        color: '#64748b',
      };
  }
}

function mapPersistedCellToLocal(
  cell: WarehouseMapCell,
): LocalWarehouseCell {
  return {
    grid_x: cell.grid_x,
    grid_y: cell.grid_y,
    cell_type: cell.cell_type,
    levels_count: cell.levels_count,
    label: cell.label,
    notes: cell.notes,
  };
}

function mapLocalCellToPayload(
  cell: LocalWarehouseCell,
): UpsertWarehouseMapCellPayload {
  return {
    grid_x: cell.grid_x,
    grid_y: cell.grid_y,
    cell_type: cell.cell_type,
    levels_count: cell.levels_count,
    label: cell.label,
    notes: cell.notes,
  };
}

function mapWarehouseMapToDraft(
  warehouseMap: WarehouseMap,
): WarehouseMapDraft {
  return {
    code: warehouseMap.code,
    name: warehouseMap.name,
    description: warehouseMap.description ?? '',
    grid_width: warehouseMap.grid_width,
    grid_height: warehouseMap.grid_height,
    status: warehouseMap.status,
    is_active: warehouseMap.is_active,
  };
}

function buildErrorMessage(
  error: unknown,
  fallback: string,
) {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: unknown;
          };
        };
      }
    ).response;

    const detail = response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  return fallback;
}

export default function IndustrialCostWarehouseLayoutView() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseMaps, setWarehouseMaps] = useState<WarehouseMap[]>([]);

  const [selectedWarehouseId, setSelectedWarehouseId] =
    useState<string>('');

  const [selectedWarehouseMapId, setSelectedWarehouseMapId] =
    useState<string>('');

  const [mapDraft, setMapDraft] =
    useState<WarehouseMapDraft | null>(null);

  const [cellsByKey, setCellsByKey] =
    useState<Record<string, LocalWarehouseCell>>({});

  const [selectedBrush, setSelectedBrush] =
    useState<WarehouseBrush>('rack');

  const [rackLevelsCount, setRackLevelsCount] =
    useState<number>(3);

  const [isPainting, setIsPainting] =
    useState<boolean>(false);

  const [selectedCellKey, setSelectedCellKey] =
    useState<string | null>(null);

  const [isLoadingWarehouses, setIsLoadingWarehouses] =
    useState<boolean>(true);

  const [isLoadingMaps, setIsLoadingMaps] =
    useState<boolean>(false);

  const [isLoadingMapDetail, setIsLoadingMapDetail] =
    useState<boolean>(false);

  const [isSaving, setIsSaving] =
    useState<boolean>(false);

  const [isDirty, setIsDirty] =
    useState<boolean>(false);

  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  const [successMessage, setSuccessMessage] =
    useState<string | null>(null);

  const [isCreatingWarehouse, setIsCreatingWarehouse] =
    useState<boolean>(false);

  const [isCreatingWarehouseMap, setIsCreatingWarehouseMap] =
    useState<boolean>(false);

  const [warehouseNameDraft, setWarehouseNameDraft] =
    useState<string>('');

  const [isWarehouseDirty, setIsWarehouseDirty] =
    useState<boolean>(false);

  const [isSavingWarehouse, setIsSavingWarehouse] =
    useState<boolean>(false);

  const [clearLayoutDialogOpen, setClearLayoutDialogOpen] =
    useState<boolean>(false);

  const [activateMapDialogOpen, setActivateMapDialogOpen] =
    useState<boolean>(false);

  const [isActivatingMap, setIsActivatingMap] =
    useState<boolean>(false);

  const [warehouseLocations, setWarehouseLocations] =
    useState<WarehouseLocation[]>([]);

  const [materialAssignments, setMaterialAssignments] =
    useState<WarehouseLocationMaterialAssignment[]>([]);

  const [
    selectedWarehouseLocationId,
    setSelectedWarehouseLocationId,
  ] = useState<string>('');

  const [assignmentMaterialLabel, setAssignmentMaterialLabel] =
    useState<string>('');

  const [assignmentSupplier, setAssignmentSupplier] =
    useState<string>('');

  const [isSavingAssignment, setIsSavingAssignment] =
    useState<boolean>(false);

  const selectedWarehouse = useMemo(
    () =>
      warehouses.find(
        (warehouse) => warehouse.id === selectedWarehouseId,
      ) ?? null,
    [selectedWarehouseId, warehouses],
  );

  useEffect(() => {
    setWarehouseNameDraft(selectedWarehouse?.name ?? '');
    setIsWarehouseDirty(false);
  }, [selectedWarehouse]);

  const configuredCells = useMemo(
    () => Object.values(cellsByKey),
    [cellsByKey],
  );

  const selectedCell = useMemo(
    () =>
      selectedCellKey
        ? cellsByKey[selectedCellKey] ?? null
        : null,
    [cellsByKey, selectedCellKey],
  );

  const selectedCellLocations = useMemo(() => {
    if (!selectedCell) return [];

    return warehouseLocations.filter(
      (location) =>
        location.grid_x === selectedCell.grid_x &&
        location.grid_y === selectedCell.grid_y,
    );
  }, [selectedCell, warehouseLocations]);

  const assignmentByLocationId = useMemo(
    () =>
      Object.fromEntries(
        materialAssignments.map((assignment) => [
          assignment.warehouse_location_id,
          assignment,
        ]),
      ),
    [materialAssignments],
  );

  const selectedWarehouseLocation = useMemo(
    () =>
      warehouseLocations.find(
        (location) =>
          location.id === selectedWarehouseLocationId,
      ) ?? null,
    [selectedWarehouseLocationId, warehouseLocations],
  );

  const selectedLocationAssignment = useMemo(
    () =>
      selectedWarehouseLocationId
        ? assignmentByLocationId[selectedWarehouseLocationId] ?? null
        : null,
    [assignmentByLocationId, selectedWarehouseLocationId],
  );

  const assignedLocationsCount = useMemo(
    () =>
      materialAssignments.filter(
        (assignment) => assignment.is_active,
      ).length,
    [materialAssignments],
  );

  const isDraftMap = mapDraft?.status === 'draft';

  const summary = useMemo(() => {
    const aisle = configuredCells.filter(
      (cell) => cell.cell_type === 'aisle',
    ).length;

    const rack = configuredCells.filter(
      (cell) => cell.cell_type === 'rack',
    ).length;

    const floorStorage = configuredCells.filter(
      (cell) => cell.cell_type === 'floor_storage',
    ).length;

    const blocked = configuredCells.filter(
      (cell) => cell.cell_type === 'blocked',
    ).length;

    const rackLocations = configuredCells
      .filter((cell) => cell.cell_type === 'rack')
      .reduce(
        (total, cell) => total + cell.levels_count,
        0,
      );

    return {
      configured: configuredCells.length,
      aisle,
      rack,
      floorStorage,
      blocked,
      locations: rackLocations + floorStorage,
    };
  }, [configuredCells]);

  const gridCoordinates = useMemo(() => {
    if (!mapDraft) return [];

    return Array.from(
      { length: mapDraft.grid_height },
      (_, gridY) =>
        Array.from(
          { length: mapDraft.grid_width },
          (_, gridX) => ({
            grid_x: gridX,
            grid_y: gridY,
          }),
        ),
    ).flat();
  }, [mapDraft]);

  useEffect(() => {
    function stopPainting() {
      setIsPainting(false);
    }

    window.addEventListener('pointerup', stopPainting);
    window.addEventListener('pointercancel', stopPainting);

    return () => {
      window.removeEventListener('pointerup', stopPainting);
      window.removeEventListener('pointercancel', stopPainting);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadWarehouses() {
      setIsLoadingWarehouses(true);
      setErrorMessage(null);

      try {
        const rows = await getIndustrialCostWarehouses();

        if (cancelled) return;

        setWarehouses(rows);

        setSelectedWarehouseId((current) => {
          if (current && rows.some((item) => item.id === current)) {
            return current;
          }

          return rows[0]?.id ?? '';
        });
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            buildErrorMessage(
              error,
              'No se pudieron cargar los almacenes.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingWarehouses(false);
        }
      }
    }

    void loadWarehouses();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMaps() {
      if (!selectedWarehouseId) {
        setWarehouseMaps([]);
        setSelectedWarehouseMapId('');
        setMapDraft(null);
        setCellsByKey({});
        return;
      }

      setIsLoadingMaps(true);
      setErrorMessage(null);

      try {
        const rows = await getIndustrialCostWarehouseMaps(
          selectedWarehouseId,
        );

        if (cancelled) return;

        setWarehouseMaps(rows);

        setSelectedWarehouseMapId((current) => {
          if (current && rows.some((item) => item.id === current)) {
            return current;
          }

          return rows[0]?.id ?? '';
        });

        if (rows.length === 0) {
          setMapDraft(null);
          setCellsByKey({});
          setWarehouseLocations([]);
          setMaterialAssignments([]);
          setSelectedWarehouseLocationId('');
          setSelectedCellKey(null);
          setIsDirty(false);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            buildErrorMessage(
              error,
              'No se pudieron cargar los planos del almacén.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMaps(false);
        }
      }
    }

    void loadMaps();

    return () => {
      cancelled = true;
    };
  }, [selectedWarehouseId]);

  useEffect(() => {
    let cancelled = false;

    async function loadMapDetail() {
      if (!selectedWarehouseMapId) {
        setMapDraft(null);
        setCellsByKey({});
        setWarehouseLocations([]);
        setMaterialAssignments([]);
        setSelectedWarehouseLocationId('');
        setSelectedCellKey(null);
        setIsDirty(false);
        return;
      }

      setIsLoadingMapDetail(true);
      setErrorMessage(null);

      try {
        const [detail, assignments] = await Promise.all([
          getIndustrialCostWarehouseMapDetail(
            selectedWarehouseMapId,
          ),
          getIndustrialCostWarehouseLocationMaterialAssignments(
            selectedWarehouseMapId,
          ),
        ]);

        if (cancelled) return;

        setMapDraft(
          mapWarehouseMapToDraft(detail.warehouse_map),
        );

        setCellsByKey(
          Object.fromEntries(
            detail.cells.map((cell) => {
              const mapped = mapPersistedCellToLocal(cell);

              return [
                buildCellKey(
                  mapped.grid_x,
                  mapped.grid_y,
                ),
                mapped,
              ];
            }),
          ),
        );

        setWarehouseLocations(detail.locations ?? []);
        setMaterialAssignments(assignments);

        setSelectedCellKey(null);
        setSelectedWarehouseLocationId('');
        setIsDirty(false);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            buildErrorMessage(
              error,
              'No se pudo cargar el detalle del plano.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMapDetail(false);
        }
      }
    }

    void loadMapDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedWarehouseMapId]);

  useEffect(() => {
    setAssignmentMaterialLabel(
      selectedLocationAssignment?.material_label ?? '',
    );

    setAssignmentSupplier(
      selectedLocationAssignment?.supplier ?? '',
    );
  }, [selectedLocationAssignment]);

  function markDirty() {
    setIsDirty(true);
    setSuccessMessage(null);
  }

  function paintCell(
    gridX: number,
    gridY: number,
  ) {
    if (!mapDraft || !selectedWarehouseMapId || !isDraftMap) {
      return;
    }

    const cellKey = buildCellKey(gridX, gridY);

    setSelectedCellKey(cellKey);

    if (selectedBrush === 'erase') {
      setCellsByKey((current) => {
        if (!current[cellKey]) return current;

        const next = { ...current };
        delete next[cellKey];

        return next;
      });

      markDirty();
      return;
    }

    setCellsByKey((current) => ({
      ...current,
      [cellKey]: {
        grid_x: gridX,
        grid_y: gridY,
        cell_type: selectedBrush,
        levels_count:
          selectedBrush === 'rack'
            ? rackLevelsCount
            : 1,
        label: current[cellKey]?.label ?? null,
        notes: current[cellKey]?.notes ?? null,
      },
    }));

    markDirty();
  }

  function handleCellPointerDown(
    event: PointerEvent<HTMLDivElement>,
    gridX: number,
    gridY: number,
  ) {
    if (
      event.button !== 0 &&
      event.pointerType !== 'touch'
    ) {
      return;
    }

    event.preventDefault();

    setIsPainting(true);
    paintCell(gridX, gridY);
  }

  function handleCellPointerEnter(
    gridX: number,
    gridY: number,
  ) {
    if (!isPainting) return;

    paintCell(gridX, gridY);
  }

  function updateSelectedRackLevels(
    nextLevelsCount: number,
  ) {
    if (
      !selectedCell ||
      selectedCell.cell_type !== 'rack' ||
      !isDraftMap
    ) {
      return;
    }

    const normalizedLevelsCount = Math.max(
      1,
      Math.min(
        Math.round(nextLevelsCount),
        12,
      ),
    );

    const cellKey = buildCellKey(
      selectedCell.grid_x,
      selectedCell.grid_y,
    );

    setCellsByKey((current) => ({
      ...current,
      [cellKey]: {
        ...selectedCell,
        levels_count: normalizedLevelsCount,
      },
    }));

    markDirty();
  }

  function updateGridWidth(nextValue: number) {
    if (!mapDraft || !isDraftMap) return;

    const normalized = clampGridSize(nextValue);

    const hasCellsOutside = configuredCells.some(
      (cell) => cell.grid_x >= normalized,
    );

    if (hasCellsOutside) {
      setErrorMessage(
        'No puedes reducir las columnas mientras existan celdas pintadas fuera del nuevo límite.',
      );
      return;
    }

    setMapDraft((current) =>
      current
        ? {
            ...current,
            grid_width: normalized,
          }
        : current,
    );

    markDirty();
  }

  function updateGridHeight(nextValue: number) {
    if (!mapDraft || !isDraftMap) return;

    const normalized = clampGridSize(nextValue);

    const hasCellsOutside = configuredCells.some(
      (cell) => cell.grid_y >= normalized,
    );

    if (hasCellsOutside) {
      setErrorMessage(
        'No puedes reducir las filas mientras existan celdas pintadas fuera del nuevo límite.',
      );
      return;
    }

    setMapDraft((current) =>
      current
        ? {
            ...current,
            grid_height: normalized,
          }
        : current,
    );

    markDirty();
  }

  function clearLayout() {
    if (!isDraftMap) return;

    setCellsByKey({});
    setSelectedCellKey(null);
    setClearLayoutDialogOpen(false);

    markDirty();
  }

  async function reloadWarehouses(
    preferredWarehouseId?: string,
  ) {
    const rows = await getIndustrialCostWarehouses();

    setWarehouses(rows);

    setSelectedWarehouseId(
      preferredWarehouseId &&
        rows.some(
          (warehouse) =>
            warehouse.id === preferredWarehouseId,
        )
        ? preferredWarehouseId
        : rows[0]?.id ?? '',
    );
  }

  async function reloadMaps(
    warehouseId: string,
    preferredMapId?: string,
  ) {
    const rows = await getIndustrialCostWarehouseMaps(
      warehouseId,
    );

    setWarehouseMaps(rows);

    setSelectedWarehouseMapId(
      preferredMapId &&
        rows.some(
          (warehouseMap) =>
            warehouseMap.id === preferredMapId,
        )
        ? preferredMapId
        : rows[0]?.id ?? '',
    );
  }

  async function handleCreateWarehouse() {
    setIsCreatingWarehouse(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const created = await createIndustrialCostWarehouse({
        is_active: true,
      });

      await reloadWarehouses(created.id);

      setSuccessMessage(
        'Almacén creado correctamente. Puedes cambiar su nombre directamente.',
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo crear el almacén.',
        ),
      );
    } finally {
      setIsCreatingWarehouse(false);
    }
  }


  async function handleCreateWarehouseMap() {
    if (!selectedWarehouseId) {
      setErrorMessage(
        'Debes seleccionar primero un almacén.',
      );
      return;
    }

    setIsCreatingWarehouseMap(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const created =
        await createIndustrialCostWarehouseMap({
          warehouse_id: selectedWarehouseId,
          grid_width: 20,
          grid_height: 20,
          status: 'draft',
          is_active: true,
        });

      await reloadMaps(
        selectedWarehouseId,
        created.id,
      );

      setSuccessMessage(
        'Plano creado correctamente. Puedes renombrarlo y comenzar a pintar.',
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo crear el plano.',
        ),
      );
    } finally {
      setIsCreatingWarehouseMap(false);
    }
  }

  async function handleSaveWarehouse() {
    if (!selectedWarehouse) return;

    const normalizedName = warehouseNameDraft.trim();

    if (!normalizedName) {
      setErrorMessage(
        'El nombre del almacén es obligatorio.',
      );
      return;
    }

    setIsSavingWarehouse(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const updated =
        await updateIndustrialCostWarehouse(
          selectedWarehouse.id,
          {
            code: selectedWarehouse.code,
            name: normalizedName,
            description:
              selectedWarehouse.description ?? null,
            is_active: selectedWarehouse.is_active,
          },
        );

      setWarehouses((current) =>
        current.map((warehouse) =>
          warehouse.id === updated.id
            ? updated
            : warehouse,
        ),
      );

      setWarehouseNameDraft(updated.name);
      setIsWarehouseDirty(false);

      setSuccessMessage(
        'Almacén actualizado correctamente.',
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo actualizar el almacén.',
        ),
      );
    } finally {
      setIsSavingWarehouse(false);
    }
  }

  async function handleSaveMaterialAssignment() {
    const normalizedMaterialLabel =
      assignmentMaterialLabel.trim();

    if (
      !selectedWarehouseLocation ||
      !normalizedMaterialLabel
    ) {
      setErrorMessage(
        'Selecciona una ubicación e introduce una descripción habitual.',
      );
      return;
    }

    setIsSavingAssignment(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const saved =
        await upsertIndustrialCostWarehouseLocationMaterialAssignment(
          selectedWarehouseLocation.id,
          {
            material_id: null,
            is_external: true,
            external_material_name:
              normalizedMaterialLabel,
            material_label:
              normalizedMaterialLabel,
            supplier:
              assignmentSupplier.trim() || null,
            display_order: 0,
            is_active: true,
          },
        );

      setMaterialAssignments((current) => {
        const exists = current.some(
          (assignment) =>
            assignment.warehouse_location_id ===
            saved.warehouse_location_id,
        );

        if (!exists) {
          return [...current, saved];
        }

        return current.map((assignment) =>
          assignment.warehouse_location_id ===
          saved.warehouse_location_id
            ? saved
            : assignment,
        );
      });

      setSuccessMessage(
        'Asignación habitual guardada correctamente.',
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo guardar la asignación habitual.',
        ),
      );
    } finally {
      setIsSavingAssignment(false);
    }
  }

  async function handleDeleteMaterialAssignment() {
    if (
      !selectedWarehouseLocation ||
      !selectedLocationAssignment
    ) {
      return;
    }

    setIsSavingAssignment(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostWarehouseLocationMaterialAssignment(
        selectedWarehouseLocation.id,
      );

      setMaterialAssignments((current) =>
        current.filter(
          (assignment) =>
            assignment.warehouse_location_id !==
            selectedWarehouseLocation.id,
        ),
      );

      setAssignmentMaterialLabel('');
      setAssignmentSupplier('');

      setSuccessMessage(
        'Asignación habitual eliminada correctamente.',
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo eliminar la asignación habitual.',
        ),
      );
    } finally {
      setIsSavingAssignment(false);
    }
  }

  async function handleActivateMap() {
    if (
      !selectedWarehouseMapId ||
      !mapDraft ||
      !isDraftMap
    ) {
      return;
    }

    if (isDirty) {
      setActivateMapDialogOpen(false);

      setErrorMessage(
        'Guarda los cambios del plano antes de activarlo.',
      );

      return;
    }

    if (summary.locations === 0) {
      setActivateMapDialogOpen(false);

      setErrorMessage(
        'El plano debe contener al menos una celda de almacenamiento.',
      );

      return;
    }

    setIsActivatingMap(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response =
        await activateIndustrialCostWarehouseMap(
          selectedWarehouseMapId,
        );

      setMapDraft(
        mapWarehouseMapToDraft(
          response.warehouse_map,
        ),
      );

      setWarehouseMaps((current) =>
        current.map((warehouseMap) =>
          warehouseMap.id === response.warehouse_map.id
            ? response.warehouse_map
            : warehouseMap,
        ),
      );

      setWarehouseLocations(response.locations);
      setActivateMapDialogOpen(false);

      setSuccessMessage(
        `Plano activado correctamente. ${response.locations_created} ubicación(es) materializadas.`,
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo activar el plano.',
        ),
      );
    } finally {
      setIsActivatingMap(false);
    }
  }

  async function handleSaveLayout() {
    if (
      !selectedWarehouseMapId ||
      !mapDraft ||
      !isDraftMap
    ) {
      return;
    }

    const normalizedCode = mapDraft.code.trim();
    const normalizedName = mapDraft.name.trim();

    if (!normalizedCode || !normalizedName) {
      setErrorMessage(
        'Código y nombre del plano son obligatorios.',
      );
      return;
    }

    const mapPayload: UpdateWarehouseMapPayload = {
      code: normalizedCode,
      name: normalizedName,
      description:
        mapDraft.description.trim() || null,
      grid_width: mapDraft.grid_width,
      grid_height: mapDraft.grid_height,
      status: 'draft',
      is_active: mapDraft.is_active,
    };

    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const updatedMap =
        await updateIndustrialCostWarehouseMap(
          selectedWarehouseMapId,
          mapPayload,
        );

      const cellsResponse =
        await replaceIndustrialCostWarehouseMapCells(
          selectedWarehouseMapId,
          {
            cells: configuredCells.map(
              mapLocalCellToPayload,
            ),
          },
        );

      setMapDraft(
        mapWarehouseMapToDraft(updatedMap),
      );

      setCellsByKey(
        Object.fromEntries(
          cellsResponse.cells.map((cell) => {
            const mapped =
              mapPersistedCellToLocal(cell);

            return [
              buildCellKey(
                mapped.grid_x,
                mapped.grid_y,
              ),
              mapped,
            ];
          }),
        ),
      );

      setWarehouseMaps((current) =>
        current.map((warehouseMap) =>
          warehouseMap.id === updatedMap.id
            ? updatedMap
            : warehouseMap,
        ),
      );

      setIsDirty(false);

      setSuccessMessage(
        `Plano guardado correctamente. ${cellsResponse.rows_saved} celda(s) persistidas.`,
      );
    } catch (error) {
      setErrorMessage(
        buildErrorMessage(
          error,
          'No se pudo guardar el plano.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Stack spacing={2.5}>
      {errorMessage ? (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      ) : null}

      {successMessage ? (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: `1px solid ${alpha('#94a3b8', 0.16)}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: '#38bdf8',
                fontWeight: 900,
                letterSpacing: 1.1,
              }}
            >
              Inventario · Espacios
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: '#f8fafc',
                fontSize: { xs: '1.45rem', md: '1.8rem' },
                fontWeight: 950,
                letterSpacing: '-0.04em',
              }}
            >
              Diseñador de almacén
            </Typography>

            <Typography
              sx={{
                mt: 0.75,
                maxWidth: 850,
                color: '#94a3b8',
                lineHeight: 1.55,
              }}
            >
              Define la distribución física mediante una cuadrícula.
              Cada celda de estantería representa una posición
              horizontal de palé y puede generar varios huecos según
              su número de alturas.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            {isDirty ? (
              <Chip
                label="Cambios sin guardar"
                size="small"
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  color: '#fde68a',
                  bgcolor: 'rgba(245,158,11,0.10)',
                  border: '1px solid rgba(251,191,36,0.22)',
                  fontWeight: 900,
                }}
              />
            ) : null}

            <Button
              variant="contained"
              startIcon={
                isSaving ? (
                  <CircularProgress
                    color="inherit"
                    size={16}
                  />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={() => void handleSaveLayout()}
              disabled={
                !selectedWarehouseMapId ||
                !isDraftMap ||
                !isDirty ||
                isSaving
              }
              sx={{
                minHeight: 42,
                fontWeight: 900,
                textTransform: 'none',
              }}
            >
              {isSaving
                ? 'Guardando...'
                : 'Guardar plano'}
            </Button>

            {isDraftMap ? (
              <Button
                variant="outlined"
                onClick={() => setActivateMapDialogOpen(true)}
                disabled={
                  isDirty ||
                  summary.locations === 0 ||
                  isSaving ||
                  isActivatingMap
                }
                sx={{
                  minHeight: 42,
                  color: '#bbf7d0',
                  borderColor: 'rgba(74,222,128,0.28)',
                  fontWeight: 900,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(74,222,128,0.48)',
                    bgcolor: 'rgba(34,197,94,0.08)',
                  },
                }}
              >
                {isActivatingMap
                  ? 'Activando...'
                  : 'Activar plano'}
              </Button>
            ) : (
              <Chip
                label={`${warehouseLocations.length} ubicaciones activas`}
                size="small"
                sx={{
                  color: '#bbf7d0',
                  bgcolor: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(74,222,128,0.24)',
                  fontWeight: 900,
                }}
              />
            )}

            <Button
              variant="outlined"
              onClick={() => setClearLayoutDialogOpen(true)}
              disabled={
                !isDraftMap ||
                configuredCells.length === 0 ||
                isSaving
              }
              sx={{
                minHeight: 42,
                color: '#fda4af',
                borderColor: 'rgba(244,63,94,0.24)',
                fontWeight: 900,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'rgba(244,63,94,0.44)',
                  bgcolor: 'rgba(244,63,94,0.08)',
                },
              }}
            >
              Limpiar plano
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: '300px minmax(0, 1fr) 280px',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3.5,
            bgcolor: 'rgba(15,23,42,0.72)',
            border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Configuración
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                }}
              >
                Selecciona un almacén y uno de sus planos.
              </Typography>
            </Box>

            {isLoadingWarehouses ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <CircularProgress size={18} />

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.82rem',
                  }}
                >
                  Cargando almacenes...
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={0.8}>
                <TextField
                  select
                  label="Almacén"
                  value={selectedWarehouseId}
                  onChange={(event) => {
                    setSelectedWarehouseId(
                      event.target.value,
                    );

                    setSelectedWarehouseMapId('');
                    setSuccessMessage(null);
                  }}
                  size="small"
                  fullWidth
                >
                  {warehouses.length === 0 ? (
                    <MenuItem value="">
                      Sin almacenes configurados
                    </MenuItem>
                  ) : null}

                  {warehouses.map((warehouse) => (
                    <MenuItem
                      key={warehouse.id}
                      value={warehouse.id}
                    >
                      {warehouse.code} · {warehouse.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="outlined"
                  startIcon={
                    isCreatingWarehouse ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <AddRoundedIcon />
                    )
                  }
                  onClick={() => void handleCreateWarehouse()}
                  disabled={isCreatingWarehouse}
                  sx={{
                    justifyContent: 'flex-start',
                    fontWeight: 900,
                    textTransform: 'none',
                  }}
                >
                  {isCreatingWarehouse
                    ? 'Creando...'
                    : 'Crear almacén'}
                </Button>

                {selectedWarehouse ? (
                  <Stack
                    spacing={0.8}
                    sx={{
                      mt: 0.6,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(2,6,23,0.22)',
                      border: '1px solid rgba(148,163,184,0.10)',
                    }}
                  >
                    <TextField
                      label="Nombre del almacén"
                      value={warehouseNameDraft}
                      onChange={(event) => {
                        setWarehouseNameDraft(event.target.value);
                        setIsWarehouseDirty(true);
                        setSuccessMessage(null);
                      }}
                      size="small"
                      fullWidth
                    />

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.7rem',
                        fontWeight: 800,
                      }}
                    >
                      Código interno: {selectedWarehouse.code}
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={
                        isSavingWarehouse ? (
                          <CircularProgress size={15} color="inherit" />
                        ) : (
                          <SaveRoundedIcon />
                        )
                      }
                      onClick={() => void handleSaveWarehouse()}
                      disabled={
                        !isWarehouseDirty ||
                        isSavingWarehouse
                      }
                      sx={{
                        justifyContent: 'flex-start',
                        fontWeight: 900,
                        textTransform: 'none',
                      }}
                    >
                      {isSavingWarehouse
                        ? 'Guardando...'
                        : 'Guardar almacén'}
                    </Button>
                  </Stack>
                ) : null}

              </Stack>
            )}

            {selectedWarehouseId ? (
              <Stack spacing={0.8}>
                {isLoadingMaps ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CircularProgress size={18} />

                    <Typography
                      sx={{
                        color: '#94a3b8',
                        fontSize: '0.82rem',
                      }}
                    >
                      Cargando planos...
                    </Typography>
                  </Stack>
                ) : (
                  <TextField
                    select
                    label="Plano"
                    value={selectedWarehouseMapId}
                    onChange={(event) => {
                      setSelectedWarehouseMapId(
                        event.target.value,
                      );

                      setSuccessMessage(null);
                    }}
                    size="small"
                    fullWidth
                  >
                    {warehouseMaps.length === 0 ? (
                      <MenuItem value="">
                        Sin planos configurados
                      </MenuItem>
                    ) : null}

                    {warehouseMaps.map((warehouseMap) => (
                      <MenuItem
                        key={warehouseMap.id}
                        value={warehouseMap.id}
                      >
                        {warehouseMap.code} · {warehouseMap.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <Button
                  variant="outlined"
                  startIcon={
                    isCreatingWarehouseMap ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <AddRoundedIcon />
                    )
                  }
                  onClick={() => void handleCreateWarehouseMap()}
                  disabled={isCreatingWarehouseMap}
                  sx={{
                    justifyContent: 'flex-start',
                    fontWeight: 900,
                    textTransform: 'none',
                  }}
                >
                  {isCreatingWarehouseMap
                    ? 'Creando...'
                    : 'Crear plano'}
                </Button>
              </Stack>
            ) : null}

            {mapDraft ? (
              <>
                <Divider
                  sx={{
                    borderColor: 'rgba(148,163,184,0.12)',
                  }}
                />

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.72rem',
                    fontWeight: 800,
                  }}
                >
                  Código interno del plano: {mapDraft.code}
                </Typography>

                <TextField
                  label="Nombre del plano"
                  value={mapDraft.name}
                  onChange={(event) => {
                    setMapDraft((current) =>
                      current
                        ? {
                            ...current,
                            name: event.target.value,
                          }
                        : current,
                    );

                    markDirty();
                  }}
                  size="small"
                  fullWidth
                  disabled={!isDraftMap}
                />

                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Columnas"
                    type="number"
                    value={mapDraft.grid_width}
                    onChange={(event) =>
                      updateGridWidth(
                        Number(event.target.value),
                      )
                    }
                    size="small"
                    fullWidth
                    disabled={!isDraftMap}
                    inputProps={{
                      min: MIN_GRID_SIZE,
                      max: MAX_GRID_SIZE,
                    }}
                  />

                  <TextField
                    label="Filas"
                    type="number"
                    value={mapDraft.grid_height}
                    onChange={(event) =>
                      updateGridHeight(
                        Number(event.target.value),
                      )
                    }
                    size="small"
                    fullWidth
                    disabled={!isDraftMap}
                    inputProps={{
                      min: MIN_GRID_SIZE,
                      max: MAX_GRID_SIZE,
                    }}
                  />
                </Stack>

                <Divider
                  sx={{
                    borderColor: 'rgba(148,163,184,0.12)',
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      mb: 1,
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Pincel
                  </Typography>

                  <Stack spacing={0.8}>
                    {BRUSH_OPTIONS.map((brush) => {
                      const selected =
                        selectedBrush === brush.value;

                      return (
                        <Button
                          key={brush.value}
                          variant={
                            selected
                              ? 'contained'
                              : 'outlined'
                          }
                          startIcon={brush.icon}
                          onClick={() =>
                            setSelectedBrush(brush.value)
                          }
                          disabled={!isDraftMap}
                          sx={{
                            justifyContent: 'flex-start',
                            minHeight: 42,
                            fontWeight: 900,
                            textTransform: 'none',
                          }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography
                              component="span"
                              sx={{
                                display: 'block',
                                fontSize: '0.84rem',
                                fontWeight: 900,
                                lineHeight: 1.1,
                              }}
                            >
                              {brush.label}
                            </Typography>

                            <Typography
                              component="span"
                              sx={{
                                display: 'block',
                                mt: 0.2,
                                fontSize: '0.7rem',
                                opacity: 0.76,
                                lineHeight: 1.1,
                              }}
                            >
                              {brush.helper}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>

                {selectedBrush === 'rack' ? (
                  <TextField
                    select
                    label="Alturas al pintar estantería"
                    value={rackLevelsCount}
                    onChange={(event) =>
                      setRackLevelsCount(
                        Number(event.target.value),
                      )
                    }
                    size="small"
                    fullWidth
                    disabled={!isDraftMap}
                  >
                    {Array.from(
                      { length: 8 },
                      (_, index) => index + 1,
                    ).map((levelsCount) => (
                      <MenuItem
                        key={levelsCount}
                        value={levelsCount}
                      >
                        {levelsCount}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 3.5,
            bgcolor: 'rgba(15,23,42,0.72)',
            border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          }}
        >
          {isLoadingMapDetail ? (
            <Stack
              spacing={1}
              alignItems="center"
              sx={{ py: 8 }}
            >
              <CircularProgress size={26} />

              <Typography
                sx={{
                  color: '#94a3b8',
                }}
              >
                Cargando distribución...
              </Typography>
            </Stack>
          ) : !selectedWarehouseMapId || !mapDraft ? (
            <Stack
              spacing={0.75}
              alignItems="center"
              sx={{
                py: 8,
                px: 2,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Todavía no hay un plano seleccionado
              </Typography>

              <Typography
                sx={{
                  maxWidth: 520,
                  color: '#94a3b8',
                  fontSize: '0.86rem',
                  lineHeight: 1.55,
                }}
              >
                Crea un almacén y un plano para comenzar a
                pintar la distribución física.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {selectedWarehouse?.name ??
                      'Almacén sin nombre'}{' '}
                    · {mapDraft.name || 'Plano sin nombre'}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: '#94a3b8',
                      fontSize: '0.78rem',
                    }}
                  >
                    {isDraftMap
                      ? 'Arrastra el dedo, lápiz o ratón para pintar.'
                      : 'Este plano no está en borrador y no puede modificarse desde el editor.'}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${mapDraft.grid_width} × ${mapDraft.grid_height}`}
                    sx={{
                      color: '#bae6fd',
                      borderColor: 'rgba(56,189,248,0.3)',
                      fontWeight: 900,
                    }}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label={mapDraft.status}
                    sx={{
                      color:
                        mapDraft.status === 'draft'
                          ? '#fde68a'
                          : '#bbf7d0',
                      borderColor:
                        mapDraft.status === 'draft'
                          ? 'rgba(251,191,36,0.28)'
                          : 'rgba(74,222,128,0.24)',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                    }}
                  />
                </Stack>
              </Stack>

              <Box
                sx={{
                  overflow: 'auto',
                  borderRadius: 2.5,
                  bgcolor: 'rgba(2,6,23,0.38)',
                  border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${mapDraft.grid_width}, ${CELL_SIZE}px)`,
                    gridAutoRows: `${CELL_SIZE}px`,
                    gap: `${CELL_GAP}px`,
                    width: 'max-content',
                    minWidth: '100%',
                    p: 1.25,
                    justifyContent: 'center',
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                >
                  {gridCoordinates.map(
                    ({ grid_x, grid_y }) => {
                      const cellKey = buildCellKey(
                        grid_x,
                        grid_y,
                      );

                      const cell =
                        cellsByKey[cellKey] ?? null;

                      const selected =
                        selectedCellKey === cellKey;

                      const visualSx =
                        getCellVisualSx(cell);

                      return (
                        <Box
                          key={cellKey}
                          title={
                            cell
                              ? `${buildCellLabel(
                                  grid_x,
                                  grid_y,
                                )} · ${getCellTypeLabel(
                                  cell.cell_type,
                                )}${
                                  cell.cell_type === 'rack'
                                    ? ` · ${cell.levels_count} alturas`
                                    : ''
                                }`
                              : `${buildCellLabel(
                                  grid_x,
                                  grid_y,
                                )} · Vacío`
                          }
                          onPointerDown={(event) =>
                            handleCellPointerDown(
                              event,
                              grid_x,
                              grid_y,
                            )
                          }
                          onPointerEnter={() =>
                            handleCellPointerEnter(
                              grid_x,
                              grid_y,
                            )
                          }
                          onClick={() =>
                            setSelectedCellKey(cellKey)
                          }
                          sx={{
                            ...visualSx,
                            position: 'relative',
                            width: CELL_SIZE,
                            minWidth: CELL_SIZE,
                            maxWidth: CELL_SIZE,
                            height: CELL_SIZE,
                            minHeight: CELL_SIZE,
                            maxHeight: CELL_SIZE,
                            borderRadius: 1.1,
                            border: '1px solid',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxSizing: 'border-box',
                            cursor: isDraftMap
                              ? selectedBrush === 'erase'
                                ? 'crosshair'
                                : 'cell'
                              : 'default',
                            outline: selected
                              ? '2px solid rgba(56,189,248,0.92)'
                              : 'none',
                            outlineOffset: selected ? 1 : 0,
                            transition:
                              'background-color 90ms ease, border-color 90ms ease, outline-color 90ms ease',
                          }}
                        >
                          {cell?.cell_type === 'rack' ? (
                            <Typography
                              component="span"
                              sx={{
                                color: 'inherit',
                                fontSize: '0.68rem',
                                fontWeight: 950,
                                lineHeight: 1,
                              }}
                            >
                              {cell.levels_count}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Box>
            </Stack>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3.5,
            bgcolor: 'rgba(15,23,42,0.72)',
            border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Resumen
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                }}
              >
                Distribución pintada actualmente.
              </Typography>
            </Box>

            <Stack spacing={0.75}>
              {[
                ['Celdas configuradas', summary.configured],
                ['Pasillos', summary.aisle],
                ['Estanterías', summary.rack],
                ['Suelo', summary.floorStorage],
                ['Bloqueadas', summary.blocked],
                ['Ubicaciones potenciales', summary.locations],
                ['Ubicaciones materializadas', warehouseLocations.length],
                ['Ubicaciones asignadas', assignedLocationsCount],
                [
                  'Ubicaciones sin asignar',
                  Math.max(
                    warehouseLocations.length -
                      assignedLocationsCount,
                    0,
                  ),
                ],
              ].map(([label, value]) => (
                <Stack
                  key={String(label)}
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      fontWeight: 800,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: '0.82rem',
                      fontWeight: 900,
                    }}
                  >
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider
              sx={{
                borderColor: 'rgba(148,163,184,0.12)',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Celda seleccionada
              </Typography>

              {selectedCell ? (
                <Stack spacing={1} sx={{ mt: 1.1 }}>
                  <Typography
                    sx={{
                      color: '#bae6fd',
                      fontWeight: 900,
                    }}
                  >
                    {buildCellLabel(
                      selectedCell.grid_x,
                      selectedCell.grid_y,
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#cbd5e1',
                      fontSize: '0.84rem',
                    }}
                  >
                    Tipo:{' '}
                    {getCellTypeLabel(
                      selectedCell.cell_type,
                    )}
                  </Typography>

                  {selectedCell.cell_type === 'rack' ? (
                    <TextField
                      select
                      label="Alturas de esta estantería"
                      value={selectedCell.levels_count}
                      onChange={(event) =>
                        updateSelectedRackLevels(
                          Number(event.target.value),
                        )
                      }
                      size="small"
                      fullWidth
                      disabled={!isDraftMap}
                    >
                      {Array.from(
                        { length: 12 },
                        (_, index) => index + 1,
                      ).map((levelsCount) => (
                        <MenuItem
                          key={levelsCount}
                          value={levelsCount}
                        >
                          {levelsCount}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                  {selectedWarehouseLocation ? (
                    <Stack
                      spacing={1}
                      sx={{
                        pt: 1,
                        borderTop: '1px solid rgba(148,163,184,0.12)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.8rem',
                          fontWeight: 900,
                        }}
                      >
                        Material habitual
                      </Typography>

                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                        }}
                      >
                        {selectedWarehouseLocation.operational_label}
                      </Typography>

                      <TextField
                        label="Descripción habitual"
                        value={assignmentMaterialLabel}
                        onChange={(event) =>
                          setAssignmentMaterialLabel(
                            event.target.value,
                          )
                        }
                        size="small"
                        fullWidth
                        placeholder="Ej. Caja pequeña"
                      />

                      <TextField
                        label="Proveedor habitual"
                        value={assignmentSupplier}
                        onChange={(event) =>
                          setAssignmentSupplier(
                            event.target.value,
                          )
                        }
                        size="small"
                        fullWidth
                        placeholder="Opcional"
                      />

                      <Stack spacing={0.75}>
                        <Button
                          variant="contained"
                          onClick={() =>
                            void handleSaveMaterialAssignment()
                          }
                          disabled={
                            !assignmentMaterialLabel.trim() ||
                            isSavingAssignment
                          }
                          sx={{
                            fontWeight: 900,
                            textTransform: 'none',
                          }}
                        >
                          {isSavingAssignment
                            ? 'Guardando...'
                            : 'Guardar asignación'}
                        </Button>

                        {selectedLocationAssignment ? (
                          <Button
                            variant="outlined"
                            startIcon={
                              <DeleteOutlineRoundedIcon />
                            }
                            onClick={() =>
                              void handleDeleteMaterialAssignment()
                            }
                            disabled={isSavingAssignment}
                            sx={{
                              color: '#fda4af',
                              borderColor:
                                'rgba(244,63,94,0.24)',
                              fontWeight: 900,
                              textTransform: 'none',
                            }}
                          >
                            Quitar asignación
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  ) : null}
                  {selectedCellLocations.length > 0 ? (
                    <Stack
                      spacing={0.65}
                      sx={{
                        pt: 1,
                        borderTop: '1px solid rgba(148,163,184,0.12)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.8rem',
                          fontWeight: 900,
                        }}
                      >
                        Ubicaciones generadas
                      </Typography>

                      {selectedCellLocations.map((location) => {
                        const assignment =
                          assignmentByLocationId[location.id];

                        const selected =
                          selectedWarehouseLocationId ===
                          location.id;

                        return (
                          <Button
                            key={location.id}
                            variant={
                              selected
                                ? 'contained'
                                : 'outlined'
                            }
                            onClick={() =>
                              setSelectedWarehouseLocationId(
                                location.id,
                              )
                            }
                            sx={{
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              px: 0.9,
                              py: 0.7,
                              borderRadius: 1.5,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  color: selected
                                    ? '#ffffff'
                                    : '#bae6fd',
                                  fontSize: '0.76rem',
                                  fontWeight: 900,
                                }}
                              >
                                {location.operational_label}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.2,
                                  color: selected
                                    ? 'rgba(255,255,255,0.78)'
                                    : '#94a3b8',
                                  fontSize: '0.68rem',
                                  fontWeight: 800,
                                }}
                              >
                                {assignment
                                  ? assignment.material_label
                                  : 'Sin material asignado'}
                              </Typography>
                            </Box>
                          </Button>
                        );
                      })}
                    </Stack>
                  ) : null}
                </Stack>
              ) : (
                <Typography
                  sx={{
                    mt: 1,
                    color: '#64748b',
                    fontSize: '0.82rem',
                  }}
                >
                  Selecciona una celda para consultar su
                  detalle.
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={activateMapDialogOpen}
        onClose={() => {
          if (!isActivatingMap) {
            setActivateMapDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Activar plano</DialogTitle>

        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              Se generarán ubicaciones estables para todos los huecos
              de almacenamiento configurados.
            </Typography>

            <Typography>
              Después de activar el plano, la cuadrícula dejará de
              poder editarse libremente.
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.84rem',
              }}
            >
              Ubicaciones que se materializarán: {summary.locations}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setActivateMapDialogOpen(false)}
            disabled={isActivatingMap}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleActivateMap()}
            disabled={isActivatingMap}
          >
            {isActivatingMap
              ? 'Activando...'
              : 'Activar plano'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearLayoutDialogOpen}
        onClose={() => setClearLayoutDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Limpiar plano</DialogTitle>

        <DialogContent>
          <Typography>
            Se eliminarán todas las celdas pintadas del borrador.
            El cambio no será definitivo hasta que guardes el plano.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setClearLayoutDialogOpen(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={clearLayout}
          >
            Limpiar plano
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}